import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../services/supabaseClient";
import { useLanguage } from "../contexts/LanguageContext";
import { librarySectionPath } from "../routing/appRoutes";

interface HistoryItem {
  reading_section_id: string;
  title: string;
  source_label: string;
  created_at: string;
}

interface HistoryProps {
  userId: string;
}

const History: React.FC<HistoryProps> = ({ userId }) => {
  const { language, t } = useLanguage();
  // State for history data and UI
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's reading history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: userReadingData, error: userReadingError } = await supabase
          .from("progress_logs")
          .select(
            `
            reading_section_id,
            created_at,
            user_reading_sections!progress_logs_reading_section_id_fkey (
              title,
              section_index,
              user_reading_items!user_reading_sections_reading_item_id_fkey (
                title,
                source_type,
                source_domain
              )
            )
          `
          )
          .eq("user_id", userId)
          .eq("event_type", "article_read")
          .not("reading_section_id", "is", null)
          .order("created_at", { ascending: false });

        if (userReadingError) {
          console.error("Error fetching user reading history:", userReadingError);
          setError(t("history.loadError"));
          return;
        }

        const userReadingHistory: HistoryItem[] = (userReadingData || []).map(
          (item: any) => {
            const section = item.user_reading_sections;
            const readingItem = section?.user_reading_items;
            const title =
              section?.title ||
              readingItem?.title ||
              `Section ${(section?.section_index || 0) + 1}`;
            return {
              reading_section_id: item.reading_section_id,
              title,
              source_label: readingItem?.source_domain || t("common.importedReading"),
              created_at: item.created_at,
            };
          }
        ).filter(
          (item): item is HistoryItem => Boolean(item.reading_section_id)
        );

        setHistory(userReadingHistory);
        console.log(`Loaded ${userReadingHistory.length} history items`);
      } catch (err) {
        console.error("Unexpected error fetching history:", err);
        setError(t("history.unexpectedError"));
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [t, userId]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return t("history.yesterday");
    } else if (diffDays < 7) {
      return t("history.daysAgo", { count: diffDays });
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return t("history.weeksAgo", { count: weeks });
    } else {
      return date.toLocaleDateString(language === "zh" ? "zh-CN" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <div className="rounded-lg bg-white p-6 shadow-lg sm:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="mb-2 text-2xl font-bold text-gray-800 sm:text-3xl"
            tabIndex={-1}
            data-route-focus
          >
            {t("history.title")}
          </h1>
          {!loading && !error && (
            <p className="text-gray-600">
              {t("history.subtitle", { count: history.length })}
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-center">
            <div className="text-lg text-gray-600 mb-4">
              {t("history.loading")}
            </div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {t("common.tryAgain")}
            </button>
          </div>
        ) : (
          <>
            {/* History List */}
            {history.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {history.map((item, index) => (
                  <Link
                    key={index}
                    to={librarySectionPath(item.reading_section_id)}
                    className="flex cursor-pointer flex-col gap-3 rounded-lg border border-gray-200 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 items-center">
                      <div className="mr-4 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <span className="text-blue-600 font-semibold">
                          {index + 1}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="break-words font-medium text-gray-800">
                          {item.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.source_label} ·{" "}
                          {t("history.readOn", { date: formatDate(item.created_at) })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-end text-gray-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              /* Empty state */
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📚</div>
                <div className="text-gray-600 text-lg mb-2">
                  {t("history.emptyTitle")}
                </div>
                <div className="text-gray-500 text-sm">
                  {t("history.emptyBody")}
                </div>
              </div>
            )}

            {/* Summary */}
            {history.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="text-center text-sm text-gray-600">
                  {t("history.openAgain")}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default History;
